import { LIMIT, OPENID_KEY } from '@/constants';
import { fetcher } from '@/services/graphql/fetcher/index';
import { Sort, VirtualCurrencyCode, type AnyObj } from '@/types';
import { businessUtils, storage } from '@/utils';
import {
  fetchActiveChannelDocument,
  fetchActiveCustomerDocument,
  fetchApplyMemberDocument,
  fetchCancelMemberDocument,
  fetchGetUserMemberDocument,
  fetchLoginExternalDocument,
  fetchMaxDiscountMembershipPlanDocument,
  fetchMemberGiftCardDocument,
  fetchMembershipCardActivationDocument,
  fetchMembershipOrdersDocument,
  fetchMembershipPlanDocument,
  fetchMembershipPlansDocument,
  fetchPersonalCenterDocument,
  fetchPhoneLoginDocument,
  fetchProductVariantToMembershipIdDocument,
  fetchPurchaseGiftCardDocument,
  fetchSendCodeDocument,
  fetchShoppingCreditsConfigDocument,
  fetchUpdateUserDocument,
  fetchUserMemberGiftCardDocument,
  fetchUserMemberGiftCardOrdersDocument,
  fetchVirtualCurrencyBalanceDocument,
  fetchVirtualCurrencyHistoryDocument,
} from '../graphql/docs';

/**
 * 获取验证码
 * @param phone - 手机号
 * @returns
 */
export const fetchSendCode = async (phone: string) => {
  const res = await fetcher.mutation(fetchSendCodeDocument, {
    phone,
  });
  const code = res.data?.sendCode?.code;
  if (code === 'OK') {
    return code;
  }
};

/**
 * 通过手机号登陆
 * @param params - 登陆参数
 * @returns
 */
export const fetchPhoneLogin = async (params: AnyObj) => {
  const cache = storage.get(OPENID_KEY);
  if (cache) {
    const args = {
      openIdKey: cache,
      phone: params.phone,
      code: params.code,
    };
    const res = await fetcher.mutation(fetchPhoneLoginDocument, { args });
    if (res.data?.phoneLogin) {
      // 删除openId缓存
      storage.remove(OPENID_KEY);
      return res.data?.phoneLogin;
    }
  }
};

/**
 * 小程序登录
 */
export const fetchLoginExternal = async (args: any) => {
  const res = await fetcher.mutation(fetchLoginExternalDocument, {
    args,
  });
  return res.data?.loginExternal;
};

/**
 * 获取当前用户数据
 */
export const fetchUserInfo = async () => {
  const res = await fetcher.query(fetchActiveCustomerDocument, {
    variables: {},
  });
  const data = res.data.value?.activeCustomer;
  if (data) return data;
};

/**
 * 更新用户数据
 */
export const fetchUpdateUser = async (input: AnyObj) => {
  const res = await fetcher.mutation(fetchUpdateUserDocument, {
    input,
  });
  const data = res.data?.updateCustomer;
  if (data) return data;
};

/**
 * 获取用户会员信息
 */
export const fetchGetUserMember = async () => {
  const res = await fetcher.query(fetchGetUserMemberDocument, {});
  const data = res.data.value?.getUserMember;
  return data;
};

/**
 * 获取会员列表数据
 */
export const fetchMemberCardList = async () => {
  const res = await fetcher.query(fetchMembershipPlansDocument, {
    variables: {
      options: { filter: { state: { eq: 'shelf' }, isShow: { eq: true } } },
    },
  });
  return res.data.value?.membershipPlans?.items;
};

/**
 * 获取购卡记录
 */
export const fetchMembershipOrders = async (skip: number) => {
  const res = await fetcher.query(fetchMembershipOrdersDocument, {
    variables: {
      options: {
        skip: LIMIT * skip,
        take: LIMIT,
        sort: {
          createdAt: Sort.DESC,
        },
        filter: { state: { notEq: 'toBePaid' } },
      },
    },
  });
  return res.data.value?.membershipOrders?.items;
};

/**
 * 获取会员详情
 * @param membershipPlanId - 会员卡id
 */
export const fetchMemberCardDetail = async (membershipPlanId: string) => {
  const res = await fetcher.query(fetchMembershipPlanDocument, {
    variables: {
      membershipPlanId,
      // options: { filter: { state: { eq: 'shelf' } } },
    },
  });
  return res.data.value?.membershipPlan;
};

/**
 * 购买会员
 * @param membershipPlanId - 会员id
 */
// export const fetchPurchaseMembership = async (membershipPlanId: string) => {
//   const res = await fetcher.mutation(
//     fetchPurchaseMembershipDocument,
//     businessUtils.placeOrder.getWechatPaymentVariables({ membershipPlanId })
//   );
//   return res.data?.purchaseMembership;
// };

/**
 * 使用会员优惠
 */
export const fetchApplyMember = async (orderId: string) => {
  const res = await fetcher.mutation(fetchApplyMemberDocument, { orderId });
  return res.data?.applyMember;
};

/**
 * 取消使用会员优惠
 */
export const fetchCancelMember = async (orderId: string) => {
  const res = await fetcher.mutation(fetchCancelMemberDocument, { orderId });
  return res.data?.cancelMember;
};

/**
 * 获取最大优惠的会员卡信息
 */
export const fetchMaxDiscountMembershipPlan = async () => {
  const res = await fetcher.query(fetchMaxDiscountMembershipPlanDocument, {});
  return res.data.value?.maxDiscountMembershipPlan;
};

/**
 * 激活会员
 * @param variables
 */
export const fetchMembershipCardActivation = async (variables: AnyObj) => {
  const res = await fetcher.mutation(fetchMembershipCardActivationDocument, variables);
  return res.data?.membershipCardActivation;
};

/**
 * 获取渠道配置信息
 */
export const fetchActiveChannel = async () => {
  const res = await fetcher.query(fetchActiveChannelDocument, {});
  return res.data.value?.activeChannel?.customFields;
};

/**
 * 获取礼品卡数据
 */
export const fetchMemberGiftCard = async (id: string) => {
  const res = await fetcher.query(fetchMemberGiftCardDocument, {
    variables: {
      giftCardId: id,
    },
  });
  return res.data.value.giftCard;
};

/**
 * 获取用户持有的礼品卡数据
 */
export const fetchUserMemberGiftCard = async () => {
  const res = await fetcher.query(fetchUserMemberGiftCardDocument, {});
  return res.data.value.userGiftCard;
};

/**
 * 购买礼品卡
 * @param giftCardId - 礼品卡id
 */
export const fetchPurchaseMemberGiftCard = async (giftCardId: string) => {
  const res = await fetcher.mutation(
    fetchPurchaseGiftCardDocument,
    businessUtils.placeOrder.getWechatPaymentVariables({ giftCardId })
  );
  return res.data?.purchaseGiftCard;
};

/**
 * 获取用户礼品卡下单数据（列表）
 */
export const fetchUserMemberGiftCardOrders = async (skip: number) => {
  const res = await fetcher.mutation(fetchUserMemberGiftCardOrdersDocument, {
    options: {
      skip: LIMIT * skip,
      take: LIMIT,
      sort: {
        createdAt: Sort.DESC,
      },
    },
  });
  return res.data.giftCardOrders?.items;
};

/**
 * 根据会员卡id查询商品数据
 * @param membershipId
 * @returns
 */
export const fetchProductVariantToMembershipId = async (membershipId: string) => {
  const res = await fetcher.query(fetchProductVariantToMembershipIdDocument, {
    variables: {
      membershipId,
    },
  });
  return res.data.value.productVariantToMembershipId;
};

/**
 * 获取个人中心配置数据
 */
export const fetchPersonalCenter = async () => {
  const res = await fetcher.query(fetchPersonalCenterDocument, {});
  return res.data.value.personalCenter;
};

/**
 * 获取用户虚拟币余额
 */
export const fetchGetUserVirtualCurrencyBalance = async () => {
  const res = await fetcher.query(fetchVirtualCurrencyBalanceDocument, {
    variables: {
      virtualCurrencyCode: VirtualCurrencyCode.shoppingCredits,
    },
  });
  const data = res.data.value?.getVirtualCurrencyBalance;
  return data;
};

/**
 * 获取虚拟币明细
 * @param options
 * @returns
 */
export const fetchVirtualCurrencyHistory = async (options: AnyObject) => {
  const res = await fetcher.query(fetchVirtualCurrencyHistoryDocument, {
    variables: {
      virtualCurrencyCode: VirtualCurrencyCode.shoppingCredits,
      options,
    },
  });
  return res.data.value?.getVirtualCurrencyHistory;
};

/**
 * 获取购物金配置信息
 * @param options
 * @returns
 */
export const fetchShoppingCreditsConfig = async () => {
  const res = await fetcher.query(fetchShoppingCreditsConfigDocument, {});
  return res.data.value?.getShoppingCreditsConfig;
};
